/** Перетаскивание полосы по вертикали в превью: субтитры и хук в ручном режиме. */
import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement } from 'react';
import { SUBTITLE_TRACK } from '@reels/shared/typography';
import { t } from '../i18n';

/**
 * `placement` — текущее положение 0…1 в пределах ручного режима. Центр полосы идёт за пальцем;
 * ручка — тот же выбор для клавиатуры и экранного диктора: стрелки двигают на 5%.
 */
export function useBandDrag(placement: number, onChange: ((placement: number) => void) | undefined, label: string) {
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const percent = Math.round(placement * 100);
  const percentRef = useRef(percent);
  percentRef.current = percent;
  const dragTo = (element: HTMLElement, clientY: number) => {
    const frame = element.closest('.frame')!.getBoundingClientRect();
    const top = (clientY - frame.top) / frame.height - SUBTITLE_TRACK.bandHeight / 2;
    const t = Math.max(0, Math.min(1, (top - SUBTITLE_TRACK.minTop) / (SUBTITLE_TRACK.maxTop - SUBTITLE_TRACK.minTop)));
    onChange!(Math.round(t * 100) / 100);
  };
  const stop = () => { draggingRef.current = false; setDragging(false); };
  const dragProps = onChange ? {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      draggingRef.current = true;
      setDragging(true);
      dragTo(event.currentTarget, event.clientY);
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => { if (draggingRef.current) dragTo(event.currentTarget, event.clientY); },
    onPointerUp: stop,
    onPointerCancel: stop,
  } : {};
  const nudge = (event: KeyboardEvent) => {
    const step = { ArrowUp: -5, ArrowDown: 5, Home: -100, End: 100 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = Math.max(0, Math.min(100, percentRef.current + step));
    percentRef.current = next;
    onChange!(next / 100);
  };
  const handle: ReactElement | null = onChange ? <span className="band-handle" role="slider" tabIndex={0}
    aria-label={label} aria-orientation="vertical" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
    aria-valuetext={t('components.band.valuetext', { percent })} onKeyDown={nudge}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v16M8 8l4-4 4 4M8 16l4 4 4-4" /></svg>
  </span> : null;
  return { dragging, dragProps, handle };
}
